import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Ban, Database, Zap, Brain, UserCheck, Download, Lock, AlertTriangle, Globe } from 'lucide-react';

/**
 * Public Privacy Policy page. Static long-form content kept in sync with
 * the Privacy Center privacy tab so it can be deep-linked from the landing
 * footer or shared without an account.
 */
var SECTIONS = [
  {
    id: 'overview',
    title: '1. Overview',
    icon: Shield,
    body: [
      'Parentum is a privacy-first AI-assisted co-parenting platform designed to help families organize parenting plans, track communication quality, and gain structured insights. We built Parentum with data minimization at its core.',
      'Parentum does not sell your personal information.',
      'We process only what is necessary and retain only sanitized structured outputs. Raw input text is temporary and deleted after processing.',
    ],
  },
  {
    id: 'collect',
    title: '2. Information We Collect',
    icon: Database,
    body: [
      ['Account data', 'Email address, name, hashed password, login activity.'],
      ['User-provided text', 'Parenting plan text pasted into the analysis tool (temporary processing only).'],
      ['AI-generated outputs', 'Sanitized plan summaries, ambiguity flags, missing clause flags, structured plan data, scores.'],
      ['Application data', 'Journal entries, calendar events, communication checks, companion interactions, compliance scores.'],
      ['System data', 'Usage logs, session data, device and browser identifiers for security.'],
    ],
  },
  {
    id: 'notCollect',
    title: '3. What We Do NOT Collect',
    icon: Ban,
    body: ['Parentum actively prevents the collection and storage of:'],
    list: [
      'Protected health information (PHI) as defined under HIPAA',
      'Medical diagnoses, treatment details, or medication information',
      'Social Security numbers',
      'Bank accounts, routing numbers, or financial account identifiers',
      'Government-issued ID numbers (driver\'s license, passport)',
      'Sealed, confidential, or shielded court records',
      'Legally restricted or protected text under applicable law',
    ],
    tail: 'Our system actively detects, flags, and sanitizes this content before AI processing. Users are also warned and required to acknowledge removal of sensitive content before analysis.',
  },
  {
    id: 'used',
    title: '4. How Data Is Used',
    icon: Zap,
    body: ['We use your data to:'],
    list: [
      'Provide platform functionality (plan analysis, scoring, journaling, calendar)',
      'Generate AI-powered summaries and structured insights',
      'Support the co-parenting scoring and compliance system',
      'Maintain security, prevent fraud, and enforce terms of service',
      'Improve system performance and reliability',
    ],
    tail: 'We do not use your data for advertising, behavioral profiling, or third-party monetization.',
  },
  {
    id: 'ai',
    title: '5. AI Processing & Data Minimization',
    icon: Brain,
    body: ['Our AI architecture follows strict data minimization principles:'],
    list: [
      ['Raw text is temporary', 'Pasted parenting plan text is held only during the active processing session.'],
      ['Pre-processing scan', 'All input is automatically scanned for sensitive content (medical terms, SSNs, financial data, sealed content).'],
      ['Sanitization', 'Detected sensitive content is replaced with neutral placeholders (e.g., [HEALTH_CONCERN], [REDACTED_ID]).'],
      ['No reverse mapping', 'We do not maintain a lookup table to recover original sensitive data from placeholders.'],
      ['Structured output only', 'AI transforms sanitized input into structured fields. Only these outputs are retained.'],
      ['Per-account isolation', 'Each family\'s data is fully isolated. No data is commingled between accounts.'],
    ],
    tail: 'Parentum is not a document storage platform. It is a processing and insight tool.',
  },
  {
    id: 'retention',
    title: '6. Data Retention Policy',
    icon: Database,
    body: [
      ['Raw pasted text', 'Temporary processing only. Deleted after user review is complete or session ends.'],
      ['Sanitized structured outputs', 'Retained for ongoing platform functionality, scoring, and user access.'],
      ['System logs', 'Retained per operational and security policy.'],
    ],
    listLead: 'Retention exceptions: Some data may be retained longer when required for:',
    list: [
      'Legal compliance and regulatory obligations',
      'Fraud and security prevention',
      'Billing and subscription records',
      'Dispute resolution',
      'Valid legal holds or subpoenas',
    ],
  },
  {
    id: 'nosale',
    title: '7. No Sale of Data',
    icon: Ban,
    callout: 'Parentum does not sell, rent, or trade your personal information.',
    body: ['This means:'],
    list: [
      'No data broker relationships',
      'No advertising-driven data sharing',
      'No third-party monetization of user data',
      'No cross-context behavioral advertising',
    ],
    tail: 'California Residents: Under the California Consumer Privacy Act (CCPA/CPRA), we confirm that we do not "sell" or "share" your personal information as defined by California law. We do not engage in cross-context behavioral advertising using your data.',
  },
  {
    id: 'thirdparty',
    title: '8. Third-Party Providers',
    icon: Globe,
    body: ['Parentum works with trusted infrastructure and service providers to deliver our platform:'],
    list: [
      ['Cloud hosting', 'Secure cloud infrastructure for application delivery'],
      ['AI processing', 'Language model providers for text analysis (input is sanitized before transmission)'],
      ['Payment processing', 'Secure payment provider for subscription billing'],
    ],
    tail: 'All third-party providers are contractually restricted from using your data independently. They may only process data on our behalf and in accordance with our privacy commitments.',
  },
  {
    id: 'connected_services',
    title: '9. Third-Party Integrations & Connected Services',
    icon: Globe,
    body: [
      'Users who choose to connect third-party services, including Google Calendar, to their Parentum account acknowledge and understand that information imported, synced, stored, transmitted, or generated through those integrations may become part of their Parentum account activity and records. While Parentum takes reasonable measures to protect user data, users should understand that account-related information, including synced calendar information, messages, journals, scheduling data, and other platform activity, may be subject to disclosure, preservation, or production in response to valid subpoenas, court orders, legal process, or other lawful governmental requests. Users are solely responsible for determining what information they choose to connect, upload, sync, store, or transmit through the platform and should avoid linking or sharing information they would not want potentially disclosed through lawful legal proceedings.',
    ],
  },
  {
    id: 'rights',
    title: '10. Privacy Rights',
    icon: UserCheck,
    body: ['All users have the right to access, download, correct, and delete their personal data. Logged-in users can manage state-specific rights, including California-enhanced rights under CCPA/CPRA, from the Privacy Center inside their account.'],
  },
  {
    id: 'download',
    title: '11. Data Download & Deletion',
    icon: Download,
    body: [
      ['Download', 'Available in Profile > Account & Privacy. Includes account info, structured plan data, summaries, scoring data, and version history. Excludes raw text, internal system logic, and other users\' data.'],
      ['Deletion', 'Available in Profile > Account & Privacy. Deletes all user data including structured outputs, companion data, journal entries, and calendar data. Billing records may be retained as legally required.'],
    ],
  },
  {
    id: 'security',
    title: '12. Security',
    icon: Lock,
    list: [
      'All data encrypted in transit (TLS) and at rest',
      'Passwords hashed with industry-standard algorithms',
      'Role-based access controls and least-privilege architecture',
      'Audit logging for access and data operations',
      'Sensitive content redacted from logs and error reports',
      'Session expiration and secure token management',
      'Data minimization enforced at the architecture level',
    ],
  },
  {
    id: 'legal',
    title: '13. Legal Requests & Subpoenas',
    icon: AlertTriangle,
    body: [
      'Parentum may preserve or disclose data when required by valid legal process, including subpoenas, court orders, or government requests.',
      'We maintain:',
    ],
    list: [
      'Auditable records of stored sanitized data',
      'Legal-hold flags to pause deletion when legally required',
      'Access and disclosure event logging',
    ],
    tail: 'Where permitted by law, we will notify users of legal requests affecting their data. We only preserve data that was retained under our policy — raw data already deleted is not recoverable.',
  },
];

function renderBodyItem(item, idx) {
  if (Array.isArray(item)) {
    return (
      <p key={idx} className="text-sm text-[#3A3566] leading-relaxed mb-2">
        <strong className="text-[#1E1A3E]">{item[0]}:</strong> {item[1]}
      </p>
    );
  }
  return <p key={idx} className="text-sm text-[#3A3566] leading-relaxed mb-2">{item}</p>;
}

function renderListItem(item, idx) {
  if (Array.isArray(item)) {
    return (
      <li key={idx} className="text-sm text-[#3A3566] leading-relaxed">
        <strong className="text-[#1E1A3E]">{item[0]}:</strong> {item[1]}
      </li>
    );
  }
  return <li key={idx} className="text-sm text-[#3A3566] leading-relaxed">{item}</li>;
}

export default function Privacy() {
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#F6F7FC]" data-testid="privacy-page">
      {/* Header */}
      <header className="border-b border-[#DDE1EE] bg-white/80 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-[#3A3566] hover:text-[#2D2560] transition-colors" data-testid="privacy-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <img src="/parentum-logo.png" alt="Parentum" className="h-6 w-auto max-w-full parentum-logo-purple-mid" />
        </div>
      </header>

      {/* Title */}
      <section className="max-w-4xl mx-auto px-6 pt-10 pb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EEEFF8] border border-[#DDE1EE] mb-4">
          <Shield className="w-4 h-4 text-[#2D2560]" />
          <span className="text-xs font-bold uppercase tracking-wider text-[#2D2560]">Privacy</span>
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1E1A3E] mb-3 leading-tight">Privacy Policy</h1>
        <p className="text-sm text-[#3A3566] leading-relaxed max-w-2xl">
          Parentum is a privacy-first co-parenting platform. This policy describes what we collect, how we use it, what we will never collect, and the controls you have over your data.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-16">
        {/* No-sale banner */}
        <div className="p-4 rounded-2xl bg-[#10B981]/10 border border-[#10B981]/25 mb-8" data-testid="privacy-no-sale-banner">
          <div className="flex items-center gap-3 mb-1">
            <Ban className="w-5 h-5 text-[#10B981]" />
            <p className="text-sm font-bold text-[#0F6A4A]">Parentum does not sell your personal information.</p>
          </div>
          <p className="text-xs text-[#0F6A4A]/80">We do not sell, rent, or trade your data to data brokers, advertisers, or any third party for monetization purposes — for all users in all states, including under the California Consumer Privacy Act (CCPA/CPRA).</p>
        </div>

        <div className="space-y-4">
          {SECTIONS.map(function (s) {
            var Icon = s.icon || Shield;
            return (
              <article
                key={s.id}
                id={s.id}
                className="rounded-2xl bg-white border border-[#DDE1EE] p-6 transition-shadow hover:shadow-md"
                data-testid={'privacy-section-' + s.id}
              >
                <div className="flex items-start gap-3 mb-3">
                  <Icon className="w-5 h-5 text-[#5A4E9E] mt-0.5 shrink-0" />
                  <h2 className="font-serif-display text-xl sm:text-2xl text-[#1E1A3E] leading-snug">{s.title}</h2>
                </div>

                {s.callout && (
                  <div className="p-3 rounded-lg bg-[#10B981]/10 border border-[#10B981]/25 mb-3">
                    <p className="text-sm font-semibold text-[#0F6A4A]">{s.callout}</p>
                  </div>
                )}

                {s.body && s.body.map(renderBodyItem)}

                {s.listLead && <p className="text-sm text-[#3A3566] leading-relaxed mt-2 mb-1">{s.listLead}</p>}

                {s.list && (
                  <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
                    {s.list.map(renderListItem)}
                  </ul>
                )}

                {s.tail && <p className="text-sm text-[#3A3566] leading-relaxed mt-3">{s.tail}</p>}
              </article>
            );
          })}
        </div>

        <p className="mt-10 text-xs text-[#6B6690] text-center">
          For privacy questions or to exercise your rights, contact <a href="mailto:Support@parentum.app" className="text-[#2D2560] underline hover:text-[#5A4E9E]">Support@parentum.app</a>.
        </p>
      </section>

      <footer className="border-t border-[#DDE1EE] py-6 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs text-[#8C86C4]">&copy; 2026 Parentum. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
